<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, PropType, Ref, watch } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmSvg,
} from 'tir-components';
import { FolderIcon, ListBulletIcon } from 'tir-style-system/icons/outline';
import {
  NamespaceModel,
  GroupModel,
  QueueTasksModel,
  SettingsBreadcrumbsModel,
  BreadcrumbProjectModel,
} from '../../models';
import { RouterLink, useRouter } from 'vue-router';
import { MenuBreadcrumbsColorEnum } from '../../enums';

const router = useRouter();

const props = defineProps({
  /**Элемент хлебных крошек */
  breadcrumb: {
    type: Object as PropType<
      BreadcrumbProjectModel | NamespaceModel | GroupModel | QueueTasksModel
    >,
    required: true,
  },
  /**Настройки */
  settings: {
    type: Object as PropType<SettingsBreadcrumbsModel>,
  },
  /**Не использовать роуты */
  noRoute: {
    type: Boolean,
    default: false,
  },
  /**Цвет */
  color: {
    type: String as PropType<MenuBreadcrumbsColorEnum>,
  },
});

/**Текущий роут */
const currentRoute = computed(() => router.currentRoute.value);
/**Элемент неймспейс */
const namespace = computed(() => (props.breadcrumb.isNamespace() ? props.breadcrumb : undefined));
/**Элемент группа */
const group = computed(() => (props.breadcrumb.isGroup() ? props.breadcrumb : undefined));
/**Элемент очередь */
const queue = computed(() => (props.breadcrumb.isQueue() ? props.breadcrumb : undefined));
/**Элемент проекта */
const project = computed(() => (props.breadcrumb.isProject() ? props.breadcrumb : undefined));

/**Есть ли дочерний элемент */
const isHaveChildren = computed(() => {
  if (namespace.value) return !!namespace.value.Group || !!namespace.value.QueueTask;
  if (group.value) return !!group.value.Group || !!group.value.QueueTask;
  return false;
});

/**Выбранны элемент */
const isSelected = computed(() => {
  if (props.settings?.QueueId)
    return (
      queue.value &&
      queue.value.Id === props.settings.QueueId &&
      currentRoute.value.name.toString().match(/kanban|list/g)
    );
  if (props.settings?.GroupId) return group.value && group.value.Id === props.settings.GroupId;
  if (props.settings?.NamespaceId)
    return namespace.value && namespace.value.Id === props.settings.NamespaceId;
  return !!project.value;
});

/**Путь */
const computedRout = computed(() => {
  if (project.value) {
    return { name: 'project', path: '', projectId: props.settings?.ProjectId };
  }
  if (namespace.value) {
    return {
      name: 'namespace',
      params: {
        namespaceId: namespace.value.Id,
        projectId: props.settings?.ProjectId,
      },
    };
  }
  if (group.value) {
    return {
      name: 'group',
      params: {
        groupId: group.value.Id,
        namespaceId: group.value.NamespaceId,
        projectId: props.settings?.ProjectId,
      },
    };
  }
  if (queue.value) {
    return {
      name: 'queue',
      params: {
        queueId: queue.value.Id,
        namespaceId: queue.value.NamespaceId,
        projectId: props.settings?.ProjectId,
      },
    };
  }
});
</script>

<template>
  <TirPmButton :variant="TirPmButtonVariantEnum.LinkState" :size="TirPmButtonSizeEnum.Small">
    <RouterLink
      v-if="computedRout"
      :to="computedRout"
      :class="['menu-breadcrumbs-item', { 'is-selected': isSelected, 'no-route': noRoute }]"
    >
      <ListBulletIcon v-if="queue" class="menu-breadcrumbs-item__icon" />
      <FolderIcon v-else-if="group" class="menu-breadcrumbs-item__icon" />
      <div v-else-if="namespace" class="menu-breadcrumbs-item__icon">
        <TirPmSvg
          :url="namespace.IconUrl"
          :color="namespace.Color"
          class="menu-breadcrumbs-item__namespace-icon"
        />
      </div>
      <div v-if="project?.Name" class="menu-breadcrumbs-item__project-image-wrap">
        <TirPmIconView
          :size="24"
          isRounded
          :isBordered="false"
          :imageLoadUrl="project?.ImageUrl"
          :colorCode="project?.PreviewColor"
        >
          <template #mainSlot>
            {{ project?.Name[0].toUpperCase() }}
          </template>
        </TirPmIconView>
      </div>

      <div
        :class="[
          'menu-breadcrumbs-item__title',
          {
            'is-selected': isSelected,
            'no-route': noRoute,
            'color--gray': color === MenuBreadcrumbsColorEnum.OnlyGray,
          },
        ]"
      >
        {{ breadcrumb.Name.length >= 30 ? breadcrumb.Name.slice(0, 30) + '...' : breadcrumb.Name }}
      </div>
    </RouterLink>
  </TirPmButton>
  <div v-if="isHaveChildren" class="menu-breadcrumbs-item__separator">/</div>

  <MenuBreadcrumbsItem
    v-if="namespace?.Group"
    :breadcrumb="namespace.Group"
    :settings="settings"
    :no-route="noRoute"
    :color="color"
  />
  <MenuBreadcrumbsItem
    v-else-if="namespace?.QueueTask"
    :breadcrumb="namespace?.QueueTask"
    :settings="settings"
    :no-route="noRoute"
    :color="color"
  />
  <MenuBreadcrumbsItem
    v-else-if="group?.Group"
    :breadcrumb="group.Group"
    :settings="settings"
    :no-route="noRoute"
    :color="color"
  />
  <MenuBreadcrumbsItem
    v-else-if="group?.QueueTask"
    :breadcrumb="group.QueueTask"
    :settings="settings"
    :no-route="noRoute"
    :color="color"
  />
</template>

<style scoped lang="scss">
.menu-breadcrumbs-item {
  display: flex;
  align-items: center;

  &:not(.is-selected, .no-route) {
    cursor: pointer;
  }

  &__icon {
    margin-right: 0.375rem;
    width: 1.5rem;
    height: 1.5rem;
    color: var(--text-primary, #272d37);
  }

  &__namespace-icon {
    font-size: 1.5rem;
  }

  &__title {
    margin-right: 1rem;
    color: #272d37;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-selected:not(.no-route),
    &.color--gray {
      color: var(--text-tertiary, #93969b);
    }
  }

  &__project-image-wrap {
    margin-right: 0.375rem;
  }

  &__project-image {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
  }
}
</style>
